import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ScoreBadge from "@/components/ScoreBadge";
import { fmtUSD, fmtNum, truncWallet } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import SelectMenu from "@/components/SelectMenu";
import { Search } from "lucide-react";

function SourceBadge({ children, tone = "zinc" }) {
  const tones = {
    emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    sky: "border-sky-500/30 bg-sky-500/10 text-sky-300",
    violet: "border-violet-500/30 bg-violet-500/10 text-violet-300",
    zinc: "border-zinc-700 bg-zinc-900 text-zinc-300",
  };
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] ${tones[tone]}`}>{children}</span>;
}

function traderSources(t) {
  const sources = t.source_metadata?.sources || [];
  const hasLeaderboard = sources.includes("leaderboard") || t.leaderboard_rank != null;
  const hasHolder = sources.includes("market_holders") || t.source_metadata?.discovered_via_market_id;
  if (hasLeaderboard && hasHolder) return { label: "Both", tone: "violet" };
  if (hasLeaderboard) return { label: "Leaderboard", tone: "emerald" };
  if (hasHolder) return { label: "Market holder", tone: "sky" };
  return { label: "Tracked", tone: "zinc" };
}

export default function Traders() {
  const [q, setQ] = useState("");
  const [minScore, setMinScore] = useState("");
  const [sort, setSort] = useState("latest_score");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["traders", { q, minScore, sort, page }],
    queryFn: async () => (
      await api.get("/traders", {
        params: {
          q: q || undefined,
          min_score: minScore || undefined,
          sort, order: "desc", page, page_size: 25,
        },
      })
    ).data,
    keepPreviousData: true,
  });

  return (
    <div className="space-y-4" data-testid="traders-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Leaderboard</div>
        <h1 className="font-heading text-2xl font-bold text-zinc-100">Traders</h1>
        <p className="text-xs text-zinc-500 mt-1">
          External Polymarket leaderboard proof and MarketTrace's internal score are shown separately.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-auto">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="input-trader-search"
            placeholder="Search wallet or name…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            className="pl-8 h-9 w-full sm:w-64 bg-zinc-900 border-zinc-800 text-sm"
          />
        </div>
        <Input
          data-testid="input-min-score"
          type="number"
          placeholder="Min score"
          value={minScore}
          onChange={(e) => { setMinScore(e.target.value); setPage(1); }}
          className="h-9 w-full sm:w-28 bg-zinc-900 border-zinc-800 text-sm"
        />
        <SelectMenu
          testid="select-trader-sort"
          value={sort}
          onChange={(v) => { setSort(v); setPage(1); }}
          className="w-full sm:w-52"
          options={[
            { value: "latest_score", label: "Sort: Score" },
            { value: "leaderboard_pnl", label: "Sort: PnL" },
            { value: "leaderboard_volume", label: "Sort: Volume" },
            { value: "last_activity_at", label: "Sort: Recent activity" },
          ]}
        />
      </div>


      <div className="grid gap-3 md:hidden">
        {isLoading && <div className="rounded-md border border-zinc-800 p-4 text-center text-xs text-zinc-500">Loading…</div>}
        {data?.items?.length === 0 && !isLoading && <div className="rounded-md border border-zinc-800 p-4 text-center text-xs text-zinc-500">No traders match your filters.</div>}
        {(data?.items || []).map((t) => {
          const src = traderSources(t);
          const ps = t.performance_summary || {};
          return (
            <Link key={t.id} to={`/app/traders/${t.id}`} className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3 hover:bg-zinc-900/40" data-testid={`trader-card-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-zinc-100">{t.public_username || t.display_name || truncWallet(t.wallet_address)}</div>
                  <div className="mt-1 font-mono-num text-[11px] text-zinc-500">#{t.internal_rank} · {t.display_wallet}</div>
                </div>
                <ScoreBadge score={t.latest_score} />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <SourceBadge tone={src.tone}>{src.label}</SourceBadge>
                <span className="text-[11px] text-zinc-500">Confidence {t.latest_confidence != null ? `${Math.round(t.latest_confidence * 100)}%` : "—"}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded border border-zinc-800 p-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Leaderboard</div>
                  <div className="font-mono-num text-emerald-300">{t.leaderboard_pnl != null ? `#${t.leaderboard_rank || "—"} · ${fmtUSD(t.leaderboard_pnl)}` : "—"}</div>
                  {t.leaderboard_volume != null && <div className="text-[10px] text-zinc-500">Vol {fmtUSD(t.leaderboard_volume)}</div>}
                </div>
                <div className="rounded border border-zinc-800 p-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">MT PnL est.</div>
                  <div className={`font-mono-num ${ps.total_pnl >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{ps.total_pnl != null ? fmtUSD(ps.total_pnl) : "—"}</div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      <div className="hidden md:block border border-zinc-800 rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-zinc-900/40 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-3 py-2">#</th>
              <th className="text-left px-3 py-2">Trader</th>
              <th className="text-left px-3 py-2">Source</th>
              <th className="text-left px-3 py-2">Wallet</th>
              <th className="text-right px-3 py-2">MT score</th>
              <th className="text-right px-3 py-2">Confidence</th>
              <th className="text-right px-3 py-2">Leaderboard</th>
              <th className="text-right px-3 py-2">MT PnL est.</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8} className="text-center text-xs text-zinc-500 py-6">Loading…</td></tr>
            )}
            {data?.items?.length === 0 && !isLoading && (
              <tr><td colSpan={8} className="text-center text-xs text-zinc-500 py-6">No traders match your filters.</td></tr>
            )}
            {(data?.items || []).map((t) => (
              <tr key={t.id} className="border-t border-zinc-800/70 hover:bg-zinc-900/40 transition-colors"
                  data-testid={`trader-row-${t.id}`}>
                <td className="px-3 py-2 font-mono-num text-zinc-400">{t.internal_rank}</td>
                <td className="px-3 py-2">
                  <Link to={`/app/traders/${t.id}`} className="text-zinc-100 hover:underline">
                    {t.public_username || t.display_name || truncWallet(t.wallet_address)}
                  </Link>
                  {t.is_demo && <span className="ml-2 text-[9px] uppercase text-orange-400">demo</span>}
                </td>
                <td className="px-3 py-2">
                  {(() => {
                    const src = traderSources(t);
                    return <SourceBadge tone={src.tone}>{src.label}</SourceBadge>;
                  })()}
                </td>
                <td className="px-3 py-2 font-mono-num text-xs text-zinc-500">{t.display_wallet}</td>
                <td className="px-3 py-2 text-right"><ScoreBadge score={t.latest_score} /></td>
                <td className="px-3 py-2 text-right font-mono-num text-xs text-zinc-400">
                  {t.latest_confidence != null ? `${Math.round(t.latest_confidence * 100)}%` : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono-num text-xs">
                  {t.leaderboard_pnl != null ? (
                    <div title={`Polymarket rank #${t.leaderboard_rank || "—"} · volume ${fmtUSD(t.leaderboard_volume || 0)}`}>
                      <div className="text-emerald-300">#{t.leaderboard_rank || "—"} · {fmtUSD(t.leaderboard_pnl)}</div>
                      <div className="text-[10px] text-zinc-500">Vol {fmtUSD(t.leaderboard_volume || 0)}</div>
                    </div>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono-num">
                  {(() => {
                    const ps = t.performance_summary || {};
                    const total = ps.total_pnl;
                    if (total == null) return "—";
                    return (
                      <span className={total >= 0 ? "text-emerald-300" : "text-rose-300"}
                            title={
                              `Realized: ${ps.realized_pnl != null ? fmtUSD(ps.realized_pnl) : "—"} | ` +
                              `Unrealized: ${ps.unrealized_pnl != null ? fmtUSD(ps.unrealized_pnl) : "—"} | ` +
                              `Resolved markets: ${ps.resolved_markets || 0} | ` +
                              `Active positions: ${ps.active_positions || 0}`
                            }>
                        {fmtUSD(total)}
                      </span>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-xs text-zinc-500">
          <div>Showing {(data.page - 1) * data.page_size + 1}–{Math.min(data.page * data.page_size, data.total)} of {data.total}</div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="btn-prev-page"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={data.page === 1}
              className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-200 h-8"
            >Prev</Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="btn-next-page"
              onClick={() => setPage((p) => p + 1)}
              disabled={data.page * data.page_size >= data.total}
              className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-200 h-8"
            >Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
